import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

function mapsKey() {
  const ds = document.documentElement.dataset;
  return ds && typeof ds.mapsKey === "string" ? ds.mapsKey.trim() : "";
}

function str(value) {
  if (typeof value === "string") {
    const v = value.trim();
    return v !== "" ? v : null;
  }
  if (typeof value === "number" && isFinite(value)) {
    return String(value);
  }
  return null;
}

function validLatLng(lat, lng) {
  return isFinite(lat) && isFinite(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this.requestSeq = 0;
  }

  cancelAutocomplete() {
    this.requestSeq++;
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _features(res) {
    return res && res.data && Array.isArray(res.data.features) ? res.data.features : [];
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip) {
      s = g.zip;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Wandelt ein Feature der Geocoder-API in das bisherige geoaddress-Format um.
  // Das Feature selbst wird nicht verändert.
  _geoaddress(feature) {
    if (!feature || !feature.properties || !feature.geometry || !Array.isArray(feature.geometry.coordinates)) {
      return null;
    }

    const p = feature.properties;
    const lng = Number(feature.geometry.coordinates[0]);
    const lat = Number(feature.geometry.coordinates[1]);
    if (!validLatLng(lat, lng)) {
      return null;
    }

    const type = str(p.type);
    const name = str(p.name);
    let street = str(p.street);
    let city = str(p.city);
    let district = str(p.district);
    const number = str(p.housenumber);
    let zip = str(p.postcode);

    if (type) {
      if (type === "street" && !street && name) {
        street = name;
      } else if (type === "city" && !city && name) {
        city = name;
      } else if (type === "district" && !district && name) {
        district = name;
      } else if (p.osm_value === "postcode" && !zip && name) {
        zip = name;
      }
    } else if (p.osm_key === "highway") {
      if (!street && name) {
        street = name;
      }
    } else if (p.osm_key === "boundary" || (p.osm_key === "place" && p.osm_value === "suburb")) {
      if (!district && name) {
        district = name;
      }
    }

    if (district && city && district === city) {
      district = null;
    }

    const geoaddress = {};
    if (street) {
      geoaddress["street"] = street;
      if (number) {
        geoaddress["number"] = number;
      }
    }
    if (zip) {
      geoaddress["zip"] = zip;
    }
    if (city) {
      geoaddress["city"] = city;
    }
    if (district) {
      geoaddress["district"] = district;
    }

    geoaddress["lat"] = lat;
    geoaddress["lng"] = lng;
    geoaddress["state"] = str(p.state);
    geoaddress["country"] = str(p.country);

    let text = this._geoaddressText(geoaddress);
    const named = name && name !== street && name !== city && name !== district && name !== zip;
    if (!text) {
      text = name || "";
    } else if (named && (type === "house" || !street) && text.indexOf(name) === -1) {
      text = name + ", " + text;
    }

    if (!text) {
      return null;
    }

    geoaddress["text"] = text;

    return geoaddress;
  }

  // Liefert ein Array mit Treffern oder null, wenn die Anfrage abgebrochen
  // oder von einer neueren Suche abgelöst wurde.
  autocomplete(q) {
    this.cancelAutocomplete();

    const query = typeof q === "string" ? q.trim() : "";
    const key = mapsKey();
    if (!query || !key) {
      return Promise.resolve([]);
    }

    const seq = this.requestSeq;
    const request = {
      source: this.axios.CancelToken.source()
    };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("key", key);

    return this.axios.get(SEARCH_URL, {
      cancelToken: request.source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      if (seq !== this.requestSeq) {
        return null;
      }

      const results = [];
      this._features(res).forEach(feature => {
        const geoaddress = this._geoaddress(feature);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });

      return results;
    }, thrown => {
      if (this.axios.isCancel(thrown) || seq !== this.requestSeq) {
        return null;
      }
      throw thrown;
    }).then(result => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
      return result;
    }, err => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
      throw err;
    });
  }

  // Liefert eine geoaddress oder null, wenn nichts ermittelt werden konnte.
  reverse(lat, lng) {
    const la = Number(lat);
    const lo = Number(lng);
    const key = mapsKey();
    if (!validLatLng(la, lo) || !key) {
      return Promise.resolve(null);
    }

    return this.axios.get(REVERSE_URL, {
      timeout: REQUEST_TIMEOUT,
      params: {
        lat: la,
        lon: lo,
        lang: "de",
        key: key
      }
    }).then(res => {
      const features = this._features(res);
      return features.length > 0 ? this._geoaddress(features[0]) : null;
    }, () => null);
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
